import { clearOutOfStockToday } from '@/api/sale-api';
import MyContainer from '@/components/MyContainer';
import { BulkReceiveModal } from '@/components/products/BulkReceiveModal';
import { Button } from '@/components/ui/button';
import { useBulkReceiveStock } from '@/hooks/product/use-bulk-receive-stock';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { getOrCreateDeviceId } from '@/lib/device-id';
import { WsEvent } from '@/types/ws-type';
import Grid from '@mui/material/Grid';
import { PackagePlus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductViewToggle } from './components/product-view-toggle';
import { SaleAssignDndProvider } from './components/sale-assign-dnd-provider';
import { SaleChannelColumn } from './components/sale-channel-column';
import { SaleMobileActionsDropdown } from './components/sale-mobile-actions-dropdown';
import { SaleOutOfStockPanel } from './components/sale-out-of-stock-panel';
import { SaleProductColumn } from './components/sale-product-column';
import { SaleTotalOrderToday } from './components/sale-total-order-today';
import {
  SaleProviders,
  useSaleOrderSummaryContext,
  useSaleSidebarData,
} from './context/sale-providers';
import { useSaleInvalidation } from './hooks/use-sale-invalidation';

function SalePageContent({
  scheduleInvalidation,
  flushInvalidation,
}: {
  scheduleInvalidation: ReturnType<typeof useSaleInvalidation>['scheduleInvalidation'];
  flushInvalidation: ReturnType<typeof useSaleInvalidation>['flushInvalidation'];
}) {
  const { t } = useTranslation('sale');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productView, setProductView] = useState<'grid' | 'list'>('grid');
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const [productIdForBulkReceive, setProductIdForBulkReceive] = useState<string | null>(null);
  const [variantCodeForBulkReceive, setVariantCodeForBulkReceive] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const {
    totalOrders,
    totalItems,
    paidAmount,
    currency,
    isLoading: isLoadingOrderToday,
    refresh: refreshOrderToday,
  } = useSaleOrderSummaryContext();

  const { setOutOfStocks, fetchLiveVideos } = useSaleSidebarData();

  const { handleBulkReceiveStock } = useBulkReceiveStock({
    onSuccess: () => {
      setProductIdForBulkReceive(null);
      setVariantCodeForBulkReceive(null);
    },
  });

  const toggleProductSelection = useCallback((productId: string) => {
    setSelectedProducts((current) => {
      if (current.includes(productId)) {
        return current.filter((id) => id !== productId);
      }
      return [...current, productId];
    });
  }, []);

  const scheduleVariantRefresh = useCallback(
    (variantId: string) => {
      scheduleInvalidation({
        variantId,
        pins: true,
        orderToday: true,
      });
    },
    [scheduleInvalidation],
  );

  const handleWsMessage = useCallback(
    (stompMsg: { body: string }) => {
      try {
        const event: WsEvent = JSON.parse(stompMsg.body);
        if (event.type === 'NEW_ORDER' || event.type === 'ORDER_UPDATED') {
          if (event.deviceId === getOrCreateDeviceId()) {
            return;
          }
          if (event.type === 'NEW_ORDER') {
            toast({
              description: t('newOrderNotification', {
                customerName: event.payload.customerName,
                orderNumber: event.payload.orderNumber,
              }),
              title: t('newOrder'),
              variant: 'success',
            });
          }
          scheduleInvalidation({ pins: true, orderToday: true });
        }
      } catch {
        // ignore malformed WS payloads
      }
    },
    [scheduleInvalidation, t],
  );

  useWebSocketChannel('/topic/orders', handleWsMessage);

  const handleOrderSummaryWs = useCallback(() => {
    scheduleInvalidation({ orderToday: true });
  }, [scheduleInvalidation]);

  useWebSocketChannel('/topic/order-summary', handleOrderSummaryWs);

  const handleClearLowStockAll = useCallback(async () => {
    setOutOfStocks([]);
    await clearOutOfStockToday();
    scheduleInvalidation({ pins: true });
  }, [scheduleInvalidation, setOutOfStocks]);

  const handleRefreshOrderToday = useCallback(async () => {
    await flushInvalidation();
    await refreshOrderToday();
    await fetchLiveVideos();
  }, [flushInvalidation, refreshOrderToday, fetchLiveVideos]);

  const onInventoryUpdate = useCallback(
    (stompMsg: { body: string }) => {
      try {
        const event: WsEvent = JSON.parse(stompMsg.body);
        if (event.type === 'INVENTORY_MOVEMENT_UPDATED') {
          scheduleInvalidation({
            variantId: event.payload,
            pins: true,
            orderToday: true,
          });
        }
      } catch {
        // ignore malformed WS payloads
      }
    },
    [scheduleInvalidation],
  );

  useWebSocketChannel('/topic/inventory-update', onInventoryUpdate);

  return (
    <MyContainer
      enableBack={false}
      disableContentPadding
      subtitle={t('description')}
      title={t('title')}
      sx={{
        '& .MuiTypography-h6': {
          display: { xs: 'none', sm: 'block' },
        },
        height: 'auto',
      }}
      contentSx={{
        height: 'auto',
        overflow: 'visible',
        overflowX: 'hidden',
      }}
      actions={
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <SaleTotalOrderToday
            totalOrders={totalOrders}
            totalItems={totalItems}
            paidAmount={paidAmount}
            currency={currency}
            className="flex-1 sm:flex-none"
            onRefresh={handleRefreshOrderToday}
            isLoading={isLoadingOrderToday}
          />

          <div className="hidden sm:flex items-center gap-2">
            <ProductViewToggle productView={productView} onChange={setProductView} />
            <Button
              size="sm"
              onClick={() => {
                setProductIdForBulkReceive(null);
                setShowBulkReceiveModal(true);
              }}
            >
              <PackagePlus className="mr-2 h-4 w-4" />
              <span>{t('receiveStock')}</span>
            </Button>
          </div>

          <SaleMobileActionsDropdown
            productView={productView}
            onProductViewChange={setProductView}
            onReceiveStock={() => {
              setProductIdForBulkReceive(null);
              setShowBulkReceiveModal(true);
            }}
          />
        </div>
      }
    >
      <div className="bg-gray-50 mb-5">
        <SaleAssignDndProvider>
          <div className="w-full max-w-full mx-auto p-2 md:p-4">
            <Grid
              container
              spacing={2}
              sx={{
                width: '100%',
                margin: 0,
                '& > .MuiGrid-item': {
                  paddingLeft: { xs: '8px', md: '16px' },
                  paddingTop: { xs: '8px', md: '16px' },
                },
              }}
            >
              <SaleProductColumn
                productView={productView}
                selectedProducts={selectedProducts}
                onToggleProductSelect={toggleProductSelection}
                isSearchOpen={isSearchOpen}
                onSearchOpenChange={setIsSearchOpen}
                scheduleVariantRefresh={scheduleVariantRefresh}
              />

              <SaleChannelColumn />
            </Grid>
          </div>
        </SaleAssignDndProvider>

        <SaleOutOfStockPanel onClear={() => void handleClearLowStockAll()} />
        <BulkReceiveModal
          isOpen={showBulkReceiveModal}
          productId={productIdForBulkReceive ?? null}
          variantCode={variantCodeForBulkReceive ?? null}
          onSubmit={handleBulkReceiveStock}
          onCancel={() => {
            setShowBulkReceiveModal(false);
            setProductIdForBulkReceive(null);
          }}
        />
      </div>
    </MyContainer>
  );
}

export default function SalePage() {
  const { registerActions, scheduleInvalidation, flushInvalidation } = useSaleInvalidation();

  return (
    <SaleProviders scheduleInvalidation={scheduleInvalidation} registerActions={registerActions}>
      <SalePageContent
        scheduleInvalidation={scheduleInvalidation}
        flushInvalidation={flushInvalidation}
      />
    </SaleProviders>
  );
}
